import EmptyState from '@components/Common/EmptyState';
import FilterRow from '@components/Common/FilterRow';
import RecordLink from '@components/Common/RecordLink';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { Box } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { formatDateTime } from '@helpers/date';

/**
 * Who clicked Apply on which external job (owner, 2026-10-06: "we need to know which candidate is
 * clicking on what"). Only signed-in visitors get through to an employer, so every row has a
 * person behind it. Read from the backend through /api/external-jobs/clicks.
 */
export interface Click {
  clickId: string;
  clickedAt?: string | null;
  jobSeekerId?: string | null;
  userType?: string | null;
  candidateName?: string | null;
  candidateEmail?: string | null;
  externalJobId?: string | null;
  title?: string | null;
  company?: string | null;
  sourceId?: string | null;
  context?: string | null;
}

const PAGE_SIZE = 50;

/** Where on the site the click came from, in words. */
export const clickFrom = (context?: string | null): string =>
  ({ search: 'Job search', city: 'City page', landing: 'Landing page', job: 'Job page' } as Record<string, string>)[
    context ?? ''
  ] ?? (context || '');

const ClicksPanel: React.FC<{ sources: { value: string; label: string }[] }> = ({ sources }) => {
  const [source, setSource] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Click[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    axios
      .get('/api/external-jobs/clicks', { params: { page, size: PAGE_SIZE, sourceId: source || undefined } })
      .then((r) => {
        setRows(r.data?.content ?? []);
        setTotal(r.data?.totalElements ?? 0);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [page, source]);

  return (
    <>
      <FilterRow>
        <SelectFormField
          required={false}
          name="clickSource"
          label="Source"
          value={source}
          options={[{ value: '', label: 'All sources' }, ...sources]}
          onChange={(_n, v) => {
            setSource(String(v));
            setPage(0);
          }}
        />
      </FilterRow>
      {failed ? (
        <EmptyState compact title="Clicks could not be loaded" body="Try again in a moment." />
      ) : loading && rows.length === 0 ? (
        <SkeletonLoader rows={6} columns={4} />
      ) : rows.length === 0 ? (
        <EmptyState compact title="No clicks yet" body="Each Apply on an employer-site job shows up here with who made it." />
      ) : (
        <MintlyTable
          pagination={{
            enabled: total > PAGE_SIZE,
            page,
            rowsPerPage: PAGE_SIZE,
            totalNumberOfRows: total,
            handleChangePage: (_e, n) => setPage(n),
          }}
          rows={rows}
          selectors={['when', 'candidate', 'job', 'from']}
          tableHeadRow={{ when: <>Clicked</>, candidate: <>Candidate</>, job: <>Job</>, from: <>From</> }}
          tableBodyRow={{
            when: (c: Click) => <>{formatDateTime(c.clickedAt, '')}</>,
            candidate: (c: Click) =>
              c.jobSeekerId && c.userType !== 'employer' ? (
                <Box className="ej-source">
                  <RecordLink to={`/jobseekers/${c.jobSeekerId}`}>
                    {c.candidateName || c.candidateEmail || c.jobSeekerId}
                  </RecordLink>
                  {c.candidateName && c.candidateEmail && <span className="ej-mono">{c.candidateEmail}</span>}
                </Box>
              ) : (
                <>{c.userType === 'employer' ? 'An employer account' : 'Unknown'}</>
              ),
            job: (c: Click) => (
              <Box className="ej-source">
                <span className="ej-source-name">{c.title}</span>
                <span className="ej-mono">
                  {c.company} · {c.sourceId}
                </span>
              </Box>
            ),
            from: (c: Click) => <>{clickFrom(c.context)}</>,
          }}
        />
      )}
    </>
  );
};

export default ClicksPanel;
